import React from 'react';
import styles from './TimelineView.module.css';
import { PlanningItem } from './types';

interface TimelineViewProps {
  items: PlanningItem[];
  onEditItem: (item: PlanningItem) => void;
  onDeleteItem?: (id: string) => void;
}

export const TimelineView: React.FC<TimelineViewProps> = ({ items, onEditItem, onDeleteItem }) => {
  const sortedItems = [...items].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  
  // Group items by month
  const groupedItems = sortedItems.reduce((acc, item) => {
    const date = new Date(item.date);
    const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
    const monthName = date.toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' });
    
    if (!acc[monthKey]) {
      acc[monthKey] = {
        name: monthName,
        items: []
      };
    }
    acc[monthKey].items.push(item);
    return acc;
  }, {} as Record<string, { name: string; items: PlanningItem[] }>);

  const getTypeColor = (type: PlanningItem['type']) => {
    switch (type) {
      case 'mijlpaal':
        return styles.typeMilestone;
      case 'communicatie':
        return styles.typeCommunication;
      case 'bijeenkomst':
        return styles.typeMeeting;
      case 'deadline':
        return styles.typeDeadline;
      default:
        return '';
    }
  };

  const getTypeLabel = (type: PlanningItem['type']) => {
    switch (type) {
      case 'mijlpaal':
        return 'Mijlpaal';
      case 'communicatie':
        return 'Communicatie';
      case 'bijeenkomst':
        return 'Bijeenkomst';
      case 'deadline':
        return 'Deadline';
      default:
        return type;
    }
  };

  if (items.length === 0) {
    return (
      <div className={styles.emptyState}>
        <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
          <line x1="8" y1="14" x2="10" y2="14" />
          <line x1="8" y1="18" x2="10" y2="18" />
          <line x1="14" y1="14" x2="16" y2="14" />
          <line x1="14" y1="18" x2="16" y2="18" />
        </svg>
        <h3>Geen planning items</h3>
        <p>Er zijn nog geen taken, mijlpalen of activiteiten gepland voor dit project.</p>
        <p className={styles.helpText}>Klik op "Nieuw item" om uw eerste planning item toe te voegen.</p>
      </div>
    );
  }

  return (
    <div className={styles.timeline}>
      {Object.entries(groupedItems).map(([monthKey, monthData]) => (
        <div key={monthKey} className={styles.monthSection}>
          <h3 className={styles.monthTitle}>{monthData.name}</h3>
          
          <div className={styles.timelineItems}>
            {monthData.items.map((item) => {
              const date = new Date(item.date);
              const dayNum = date.getDate();
              const dayName = date.toLocaleDateString('nl-NL', { weekday: 'short' });
              
              return (
                <div key={item.id} className={styles.timelineItem}>
                  <div className={styles.timelineDate}>
                    <div className={styles.dayNumber}>{dayNum}</div>
                    <div className={styles.dayName}>{dayName}</div>
                  </div>
                  
                  <div className={styles.timelineMarker}>
                    <div className={styles.markerDot}></div>
                    <div className={styles.markerLine}></div>
                  </div>
                  
                  <div className={styles.timelineCard}>
                    <div className={styles.cardContent} onClick={() => onEditItem(item)}>
                      <div className={styles.cardHeader}>
                        <span className={`${styles.typeChip} ${getTypeColor(item.type)}`}>
                          {getTypeLabel(item.type)}
                        </span>
                        {item.werkCategorie === 'raakvlak' && (
                          <span className={styles.raakvlakBadge}>Raakvlak</span>
                        )}
                        <span className={styles.time}>{item.time}</span>
                      </div>
                    
                    <h4 className={styles.cardTitle}>{item.title}</h4>
                    
                    <div className={styles.cardMeta}>
                      <div className={styles.metaItem}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                          <circle cx="9" cy="7" r="4" />
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                        </svg>
                        {item.targetAudience}
                      </div>
                      
                      {item.channel && (
                        <div className={styles.metaItem}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                            <polyline points="22,6 12,13 2,6" />
                          </svg>
                          {item.channel}
                        </div>
                      )}
                      
                      <div className={styles.metaItem}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                        {item.responsible}
                      </div>
                    </div>
                    
                    <div className={styles.cardStatus}>
                      <span className={`${styles.statusChip} ${styles[`status${item.status.replace(/\s/g, '')}`]}`}>
                        {item.status}
                      </span>
                    </div>
                    </div>
                    {onDeleteItem && (
                      <div className={styles.cardActions}>
                        <button
                          className={styles.deleteBtn}
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteItem(item.id);
                          }}
                          title="Verwijderen"
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            <line x1="10" y1="11" x2="10" y2="17" />
                            <line x1="14" y1="11" x2="14" y2="17" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};